import { useMemo } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Close from '@mui/icons-material/Close';
import { DialogSearchableMultiSelect, IconButton, Text } from 'components/ui';
import type { DialogSearchableMultiSelectValue } from 'components/ui';
import type { DashboardFieldOption } from '../dashboardEditFields.types';
import {
  DashboardControlStack,
  DashboardEmptyHint,
} from './dashboardControls.styles';
import DashboardSortableRow from './DashboardSortableRow';
import { useDashboardSortableSensors } from './useDashboardSortableSensors';

interface DashboardOrderedListFieldProps {
  addLabel: string;
  disabled?: boolean;
  minItems?: number;
  onChange: (next: string[]) => void;
  options: readonly DashboardFieldOption[];
  testId?: string;
  value: readonly string[];
}

// Order is meaningful for all three consumers — metrics, series, and a table's index-keyed columns —
// so this is a picker plus a sortable list rather than a plain multi-select.
//
// DialogSearchableMultiSelect is used as the picker only: with triggerVariant="addButton" the select
// field is skipped, and with selectedPlacement="field" its own chip list is skipped too, leaving
// just the Add button. Its menu still reflects and toggles the selection, so removing through the
// menu works as well as the per-row button. What it cannot do is reorder — hence the list below it.
const DashboardOrderedListField = ({
  addLabel,
  disabled = false,
  minItems = 0,
  onChange,
  options,
  testId,
  value,
}: DashboardOrderedListFieldProps) => {
  const sensors = useDashboardSortableSensors();
  const optionByValue = useMemo(
    () => new Map(options.map(option => [option.value, option])),
    [options]
  );
  const pickerOptions = useMemo(
    () =>
      options.map(option => ({
        // A legacy option stays choosable: it is only listed because the stored definition
        // already names it, and the user needs a way to re-pick it after browsing away.
        disabled: Boolean(option.disabledReason) && !option.isLegacy,
        group: option.group,
        id: option.value,
        label: option.label,
        secondaryLabel: option.disabledReason,
      })),
    [options]
  );
  const canRemove = value.length > minItems;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = value.indexOf(String(active.id));
    const to = value.indexOf(String(over.id));
    if (from === -1 || to === -1) {
      return;
    }
    onChange(arrayMove([...value], from, to));
  };

  const handlePickerChange = (ids: DialogSearchableMultiSelectValue[]) => {
    const next = ids.map(String);
    // The picker appends, so an existing selection keeps its order; a removal that would breach
    // minItems is ignored rather than silently letting an invalid payload through.
    if (next.length < minItems) {
      return;
    }
    onChange(next);
  };

  return (
    <DashboardControlStack>
      {value.length === 0 && <DashboardEmptyHint>Nothing selected yet.</DashboardEmptyHint>}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={[...value]} strategy={verticalListSortingStrategy}>
          {value.map(entry => {
            const option = optionByValue.get(entry);
            const label = option?.label ?? entry;

            return (
              <DashboardSortableRow
                key={entry}
                disabled={disabled || value.length < 2}
                id={entry}
                action={
                  <IconButton
                    aria-label={`Remove ${label}`}
                    disabled={disabled || !canRemove}
                    onClick={() => onChange(value.filter(current => current !== entry))}
                    size="small">
                    <Close fontSize="inherit" />
                  </IconButton>
                }>
                <Text variant="body2">{label}</Text>
                {/* Echoed on the selected row too, so a stored-but-unsuitable metric explains
                    itself without the user having to open the picker to find out. */}
                {option?.disabledReason && (
                  <DashboardEmptyHint>{option.disabledReason}</DashboardEmptyHint>
                )}
              </DashboardSortableRow>
            );
          })}
        </SortableContext>
      </DndContext>
      <DialogSearchableMultiSelect
        addButtonLabel={addLabel}
        clearSearchAriaLabel="Clear search"
        disabled={disabled}
        emptyLabel="No matches"
        itemLabelPlural="options"
        itemLabelSingular="option"
        onChange={handlePickerChange}
        options={pickerOptions}
        placeholder={addLabel}
        searchPlaceholder="Search"
        selectTestId={testId}
        selectedIds={value}
        selectedPlacement="field"
        triggerVariant="addButton"
        // Metric vocabularies run to ~125 options; the 10-row default would hide almost all of them
        // behind "Show more" on every open.
        visibleLimit={25}
      />
    </DashboardControlStack>
  );
};

export default DashboardOrderedListField;
